/**
 * PKCE (Proof Key for Code Exchange) utilities for OAuth 2.0
 */

/**
 * Generate a random code verifier string
 * Characters: a-z, A-Z, 0-9, _, -
 * Length: 43-128 characters
 */
export function generateCodeVerifier(length: number = 64): string {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);

  let result = '';
  for (let i = 0; i < length; i++) {
    result += charset[randomValues[i] % charset.length];
  }
  return result;
}

/**
 * Generate code challenge from code verifier using SHA-256
 * Returns base64url encoded hash
 */
export async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);

  // Convert to base64url
  const hashArray = Array.from(new Uint8Array(digest));
  const base64 = btoa(String.fromCharCode.apply(null, hashArray));
  return base64
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Generate a random state string for CSRF protection
 */
export function generateState(length: number = 32): string {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);

  let result = '';
  for (let i = 0; i < length; i++) {
    result += charset[randomValues[i] % charset.length];
  }
  return result;
}

/**
 * Store PKCE data in sessionStorage
 */
export function storePKCEData(codeVerifier: string, state: string): void {
  sessionStorage.setItem('vk_code_verifier', codeVerifier);
  sessionStorage.setItem('vk_state', state);
}

/**
 * Retrieve and clear PKCE data from sessionStorage
 */
export function retrievePKCEData(): { codeVerifier: string | null; state: string | null } {
  const codeVerifier = sessionStorage.getItem('vk_code_verifier');
  const state = sessionStorage.getItem('vk_state');

  // Clear after retrieval for security
  sessionStorage.removeItem('vk_code_verifier');
  sessionStorage.removeItem('vk_state');

  return { codeVerifier, state };
}
